import { Card } from "../UI/Card";
import { Button } from "../UI/Button";
import { Badge } from "../UI/Badge";
import { CheckCircle2, ChevronRight, Circle, Clock } from "lucide-react";
import { useHistory } from "react-router-dom";

interface AssessmentCardProps {
  title: string;
  status: "completed" | "in-progress" | "not-started";
  icon: React.ReactNode;
  result?: string;
  completeLabel?: string;
  listComplete?: any[];
  label?: string;
  count?: number | null;
  onStart?: () => void;
  onViewResult?: () => void;
}

export const AssessmentCard = ({
  title,
  status,
  icon,
  result,
  completeLabel = 'Selesai',
  listComplete,
  label,
  count,
  onStart,
  onViewResult,
}: AssessmentCardProps) => {
  const history = useHistory();
  const getStatusInfo = () => {
    switch (status) {
      case "completed":
        return {
          icon: <CheckCircle2 className="h-4 w-4" />,
          label: completeLabel,
          badgeClass: "bg-success/10 text-success border-success/20",
        };
      case "in-progress":
        return {
          icon: <Clock className="h-4 w-4" />,
          label: "Sedang Proses",
          badgeClass: "bg-warning/10 text-warning border-warning/20",
        };
      default:
        return {
          icon: <Circle className="h-4 w-4" />,
          label: "Belum Diambil",
          badgeClass: "bg-muted text-muted-foreground",
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Card className="p-5 pt-9 hover:shadow-md transition-shadow relative overflow-hidden">
      {label && (
        <div className="absolute top-2 right-2">
          <Badge
            variant="outline"
            className={`bg-accent/10 text-accent border-accent/20`}
          >
            <span className="ml-1">{label}</span>
          </Badge>
        </div>
      )}
      <div className="flex items-start gap-4">
        <div className="p-3 bg-primary/10 rounded-lg text-primary">{icon}</div>

        <div className="flex-1 min-w-0 pr-5">
          <h4 className="font-semibold mb-2 text-base">{title}</h4>
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge variant="outline" className={`${statusInfo.badgeClass}`}>
              {statusInfo.icon}
              <span className="ml-1">{statusInfo.label}</span>
            </Badge>
            {count && count > 0 && (
              <>
                <Badge
                  variant="outline"
                  className={`bg-primary/10 text-primary border-primary/20`}
                >
                  <span className="ml-1">{count} Peserta</span>
                </Badge>
              </>
            )}
          </div>

          {result && status === "completed" && (
            <p className="text-sm text-muted-foreground mb-3">{result}</p>
          )}

          {status === "completed" && !listComplete ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={onViewResult}
            >
              Lihat Hasil
            </Button>
          ) : (
            <Button
              size="sm"
              className="w-full gradient-primary border-0"
              onClick={onStart}
            >
              {status === "in-progress" ? "Lanjutkan Tes" : "Mulai Tes"}
            </Button>
          )}
        </div>
      </div>
      {listComplete && listComplete?.length > 0 && status === "completed" && (
        <div className="flex flex-col gap-2 mt-4 border-t border-t-primary/30">
          {listComplete?.map((item, index) => (
            <div
              className="flex justify-between items-center border-b border-b-primary/30 cursor-pointer py-3"
              onClick={() => {
                history.push(`/competency/${item?.id}`);
              }}
              key={`comp-i-${index}`}
            >
              <span className="font-semibold text-primary text-wrap">
                {item?.title}
              </span>
              <div className="font-bold text-secondary inline-flex items-center">
                {item?.isOnProgress ? (
                  <>
                    <Badge variant="outline">Lanjutkan</Badge>
                  </>
                ) : (
                  <span className="text-xl">{item?.score}</span>
                )}
                <ChevronRight size="1.25rem" />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
